import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
} from 'recharts';
import { formatPct } from '../../utils/formatPercentage';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import OrganizationsComparisonSection from '../../components/OrganizationsComparisonSection';
import { useRoleNavigation } from '../../hooks';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { organizationsApi } from '../../services/apiService';
import { OrganizationComparison } from '../../types';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useStrategyComparisonQuery } from '../../hooks/queries';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Types
interface OrganizationReport extends OrganizationComparison {
  rank: number;
  rating: 'excellent' | 'average' | 'poor';
}

type TabType = 'overview' | 'comparison' | 'charts' | 'ranking';

// Helper function to get rating based on achievement percentage
// Matches Figma design: ممتاز (>=70%), وسط (50-69%), ضعيف (<50%)
const getRating = (achievement: number): 'excellent' | 'average' | 'poor' => {
  if (achievement >= 70) return 'excellent';
  if (achievement >= 50) return 'average';
  return 'poor';
};

// Helper function to get rating label - uses translation function
const getRatingLabel = (rating: 'excellent' | 'average' | 'poor', t: (key: string) => string): string => {
  switch (rating) {
    case 'excellent': return t('reports.excellent');
    case 'average': return t('reports.average');
    case 'poor': return t('reports.poor');
  }
};

// Helper function to get rating color
const getRatingColor = (rating: 'excellent' | 'average' | 'poor'): string => {
  switch (rating) {
    case 'excellent': return '#22C55E';
    case 'average': return '#F59E0B';
    case 'poor': return '#EF4444';
  }
};

// Tab Button Component
const TabButton: React.FC<{
  label: string;
  isActive: boolean;
  onPress: () => void;
}> = ({ label, isActive, onPress }) => (
  <TouchableOpacity
    style={[
      styles.tabButton,
      isActive && styles.tabButtonActive,
    ]}
    onPress={onPress}
  >
    <Text style={[styles.tabButtonText, isActive && styles.tabButtonTextActive]}>
      {label}
    </Text>
  </TouchableOpacity>
);

// Filter Dropdown Component - Web only
const FilterDropdown: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => {
  // Web-specific styles that can't be in StyleSheet
  const webSelectStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '10px 12px 10px 36px',
    fontSize: 14,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'right' as const,
    width: '100%',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: 'none',
    direction: 'rtl',
    cursor: 'pointer',
    outline: 'none',
  };

  return (
    <View style={styles.filterContainer}>
      <select
        style={webSelectStyle}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <View style={styles.filterArrow} pointerEvents="none">
        <Ionicons name="chevron-down" size={16} color={COLORS.neutral500} />
      </View>
    </View>
  );
};

// Progress Ring Component for achievement percentage
const ProgressRing: React.FC<{ percentage: number; size?: number }> = ({ percentage, size = 36 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (percentage >= 70) return '#22C55E';
    if (percentage >= 50) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <View style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </View>
  );
};

// Table Row Component - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view
const TableRow: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => (
  <TouchableOpacity style={styles.tableRow} onPress={onPress}>
    {/* الترتيب - center */}
    <View style={[styles.tableCell, styles.rankCell]}>
      <Text style={styles.rankText}>{org.rank}</Text>
    </View>
    {/* الجهة - right align */}
    <View style={[styles.tableCell, styles.nameCell]}>
      <Text style={styles.orgName}>{translateOrg(org.name)}</Text>
    </View>
    {/* عدد المؤشرات - center */}
    <View style={[styles.tableCell, styles.kpiCountCell]}>
      <Text style={styles.tableCellText}>{org.total_kpis}</Text>
    </View>
    {/* جيد - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_good}</Text>
    </View>
    {/* متوسط - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_average}</Text>
    </View>
    {/* ضعيف - center */}
    <View style={[styles.tableCell, styles.kpiStatusCell]}>
      <Text style={styles.tableCellText}>{org.kpis_poor}</Text>
    </View>
    {/* مؤشر الالتزام - center */}
    <View style={[styles.tableCell, styles.achievementCell]}>
      <View style={styles.achievementCellContent}>
        <ProgressRing percentage={org.commitment_index} size={28} />
        <Text style={styles.achievementText}>{formatPct(org.commitment_index)} %</Text>
      </View>
    </View>
    {/* المبادرات المكتملة - center */}
    <View style={[styles.tableCell, styles.initiativesCell]}>
      <Text style={styles.tableCellText}>{org.initiatives_completed}/{org.total_initiatives}</Text>
    </View>
    {/* التقييم - center */}
    <View style={[styles.tableCell, styles.ratingCell]}>
      <View style={[styles.ratingBadge, { borderColor: getRatingColor(org.rating) }]}>
        <Text style={[styles.ratingText, { color: getRatingColor(org.rating) }]}>
          {getRatingLabel(org.rating, t)}
        </Text>
      </View>
    </View>
    {/* View button */}
    <View style={[styles.tableCell, styles.viewCell]}>
      <TouchableOpacity style={styles.viewButton}>
        <Ionicons name="eye-outline" size={18} color={COLORS.neutral400} />
      </TouchableOpacity>
    </View>
  </TouchableOpacity>
);

// Organization Comparison Card Component for مقارنة تفصيلية tab
const ComparisonCard: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => {
  const { colors } = useTheme();
  const cardStyle = { backgroundColor: colors.card, borderColor: colors.cardBorder };
  // Use real data from API
  const totalKpis = org.total_kpis || 0;
  const kpisGood = org.kpis_good || 0;
  const kpisAverage = org.kpis_average || 0;
  const kpisPoor = org.kpis_poor || 0;
  const totalGoals = org.total_goals || 0;
  const goalsAchieved = org.goals_achieved || 0;
  const totalInitiatives = org.total_initiatives || 0;
  const initiativesCompleted = org.initiatives_completed || 0;
  const commitmentIndex = org.commitment_index || 0;

  return (
    <TouchableOpacity style={[styles.comparisonCard, cardStyle]} onPress={onPress}>
      {/* Organization Name */}
      <View style={styles.comparisonCardHeader}>
        <Text style={styles.comparisonCardTitle}>{translateOrg(org.name)}</Text>
        {/* Rating Badge */}
        <View style={[styles.comparisonRatingBadge, { borderColor: getRatingColor(org.rating) }]}>
          <Text style={[styles.comparisonRatingText, { color: getRatingColor(org.rating) }]}>
            {getRatingLabel(org.rating, t)}
          </Text>
        </View>
      </View>

      {/* KPI Stats Grid */}
      <View style={styles.comparisonStatsGrid}>
        {/* Row 1 */}
        <View style={styles.comparisonStatsRow}>
          <View style={[styles.comparisonStatBox, styles.statBoxGreen]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.good')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisGood}</Text>
          </View>
          <View style={[styles.comparisonStatBox, styles.statBoxDefault]}>
            <Text style={styles.comparisonStatLabel}>{t('kpis.title')}</Text>
            <Text style={styles.comparisonStatValue}>{totalKpis}</Text>
          </View>
        </View>

        {/* Row 2 */}
        <View style={styles.comparisonStatsRow}>
          <View style={[styles.comparisonStatBox, styles.statBoxRed]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.poor')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisPoor}</Text>
          </View>
          <View style={[styles.comparisonStatBox, styles.statBoxYellow]}>
            <Text style={styles.comparisonStatLabel}>{t('reports.average')}</Text>
            <Text style={styles.comparisonStatValue}>{kpisAverage}</Text>
          </View>
        </View>
      </View>

      {/* Commitment Index Progress Bar */}
      <View style={styles.comparisonProgressSection}>
        <View style={styles.comparisonProgressHeader}>
          <Text style={[
            styles.comparisonProgressValue,
            { color: commitmentIndex >= 70 ? '#22C55E' : commitmentIndex >= 50 ? '#F59E0B' : '#EF4444' }
          ]}>
            %{formatPct(commitmentIndex)}
          </Text>
          <Text style={styles.comparisonProgressLabel}>{t('organizations.commitmentIndexLabel')}</Text>
        </View>
        <View style={styles.comparisonProgressBarBg}>
          <View
            style={[
              styles.comparisonProgressBarFill,
              {
                width: `${Math.min(commitmentIndex, 100)}%`,
                backgroundColor: commitmentIndex >= 70 ? '#22C55E' : commitmentIndex >= 50 ? '#F59E0B' : '#EF4444',
              },
            ]}
          />
        </View>
      </View>

      {/* Bottom Stats */}
      <View style={styles.comparisonBottomStats}>
        <View style={styles.comparisonBottomRow}>
          <Text style={styles.comparisonBottomValue}>{goalsAchieved}/{totalGoals}</Text>
          <Text style={styles.comparisonBottomLabel}>{t('reports.goalsAchieved')}</Text>
        </View>
        <View style={styles.comparisonBottomRow}>
          <Text style={styles.comparisonBottomValue}>{initiativesCompleted}/{totalInitiatives}</Text>
          <Text style={styles.comparisonBottomLabel}>{t('reports.initiativesCompleted')}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// Custom Tooltip Component for the chart
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <View style={styles.tooltipContainer}>
        <Text style={styles.tooltipLabel}>{label}</Text>
        {payload.map((entry: any, index: number) => (
          <View key={index} style={styles.tooltipRow}>
            <View style={[styles.tooltipDot, { backgroundColor: entry.color }]} />
            <Text style={styles.tooltipText}>
              {entry.name}: %{entry.value}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
};

// Custom Legend Component for Arabic labels
const CustomLegend: React.FC<{ t: (key: string) => string }> = ({ t }) => (
  <View style={styles.chartLegend}>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: '#22C55E' }]} />
      <Text style={styles.legendText}>{t('reports.excellent')}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: '#F59E0B' }]} />
      <Text style={styles.legendText}>{t('reports.average')}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: '#EF4444' }]} />
      <Text style={styles.legendText}>{t('reports.poor')}</Text>
    </View>
  </View>
);

// Multi-line horizontal x-axis tick: wraps long org names across up to 3 lines
// instead of rotating them. Splits on whitespace, ~14 chars per line.
const WrappedXAxisTick: React.FC<any> = ({ x, y, payload }) => {
  const text: string = (payload?.value ?? '') as string;
  const maxCharsPerLine = 14;
  const maxLines = 3;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    if (!current) {
      current = w;
    } else if ((current + ' ' + w).length <= maxCharsPerLine) {
      current += ' ' + w;
    } else {
      lines.push(current);
      current = w;
    }
    if (lines.length >= maxLines) break;
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === maxLines && words.join(' ').length > lines.join(' ').length) {
    lines[maxLines - 1] = lines[maxLines - 1].slice(0, maxCharsPerLine - 1) + '…';
  }
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill="#6B7280"
      fontSize={11}
      fontFamily="JF-Flat-Regular"
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 14 : 13}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

// Stacked Bar Chart Component using recharts for رسومات بيانية tab
const StackedBarChart: React.FC<{
  data: OrganizationReport[];
  height?: number;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ data, height = 350, translateOrg, t }) => {
  // Transform data: bar total = commitment_index, sliced by KPI bucket share.
  // Sort desc by commitment_index so the bar order matches the bar heights.
  const chartData = [...data]
    .sort((a, b) => (b.commitment_index || 0) - (a.commitment_index || 0))
    .map((org) => {
      const ci = org.commitment_index || 0;
      const totalKpis = org.total_kpis || 1;
      const goodShare = (org.kpis_good || 0) / totalKpis;
      const averageShare = (org.kpis_average || 0) / totalKpis;
      const poorShare = (org.kpis_poor || 0) / totalKpis;
      const fullName = translateOrg(org.name);
      return {
        name: fullName,
        fullName,
        excellent: Number((goodShare * ci).toFixed(2)),
        average: Number((averageShare * ci).toFixed(2)),
        poor: Number((poorShare * ci).toFixed(2)),
      };
    });

  if (data.length === 0) return null;

  return (
    <View style={styles.chartContainer}>
      <View style={{ width: '100%', height, minWidth: 300, minHeight: height }}>
        <ResponsiveContainer width="100%" height={height} debounce={50}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
            <XAxis
              dataKey="name"
              tick={<WrappedXAxisTick />}
              tickLine={false}
              axisLine={false}
              height={64}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280', fontFamily: FONTS.numbers }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `%${value}`}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="poor"
              stackId="a"
              fill="#EF4444"
              radius={[0, 0, 4, 4]}
              name={t('reports.poor')}
            />
            <Bar
              dataKey="average"
              stackId="a"
              fill="#F59E0B"
              radius={[0, 0, 0, 0]}
              name={t('reports.average')}
            />
            <Bar
              dataKey="excellent"
              stackId="a"
              fill="#22C55E"
              radius={[4, 4, 0, 0]}
              name={t('reports.excellent')}
            />
          </BarChart>
        </ResponsiveContainer>
      </View>
      <CustomLegend t={t} />
    </View>
  );
};

// Performance Comparison Stacked Bar Chart Component (مقارنة أداء الجهات)
// Shows achieved vs remaining percentage for each organization
const PerformanceComparisonChart: React.FC<{
  data: OrganizationReport[];
  height?: number;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ data, height = 350, translateOrg, t }) => {
  // Transform data for stacked bar chart - achieved vs remaining
  const chartData = [...data]
    .sort((a, b) => b.vision_achievement - a.vision_achievement)
    .map((org) => {
      const achieved = Number(Number(org.vision_achievement).toFixed(2));
      const remaining = 100 - achieved;
      const translatedName = translateOrg(org.name);

      return {
        name: translatedName.length > 15 ? translatedName.substring(0, 15) + '...' : translatedName,
        fullName: translateOrg(org.name),
        achieved: achieved,
        remaining: remaining,
      };
    });

  // Custom legend for stacked chart
  const StackedLegend = () => (
    <View style={styles.chartLegend}>
      <View style={styles.legendItem}>
        <View style={[styles.legendDot, { backgroundColor: '#22C55E' }]} />
        <Text style={styles.legendText}>{t('reports.achieved')}</Text>
      </View>
      <View style={styles.legendItem}>
        <View style={[styles.legendDot, { backgroundColor: '#E5E7EB' }]} />
        <Text style={styles.legendText}>{t('reports.remaining')}</Text>
      </View>
    </View>
  );

  if (data.length === 0) return null;

  return (
    <View style={styles.chartContainer}>
      <View style={{ width: '100%', height, minWidth: 300, minHeight: height }}>
        <ResponsiveContainer width="100%" height={height} debounce={50}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#6B7280', fontFamily: 'JF-Flat-Regular' }}
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#6B7280', fontFamily: FONTS.numbers }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `%${value}`}
              domain={[0, 100]}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <View style={styles.tooltipContainer}>
                      <Text style={styles.tooltipLabel}>{payload[0].payload.fullName}</Text>
                      <View style={styles.tooltipRow}>
                        <View style={[styles.tooltipDot, { backgroundColor: '#22C55E' }]} />
                        <Text style={styles.tooltipText}>{t('reports.achieved')}: %{payload[0].value}</Text>
                      </View>
                      <View style={styles.tooltipRow}>
                        <View style={[styles.tooltipDot, { backgroundColor: '#E5E7EB' }]} />
                        <Text style={styles.tooltipText}>{t('reports.remaining')}: %{payload[1]?.value}</Text>
                      </View>
                    </View>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="achieved"
              stackId="a"
              fill="#22C55E"
              radius={[0, 0, 4, 4]}
              name={t('charts.achieved')}
            />
            <Bar
              dataKey="remaining"
              stackId="a"
              fill="#E5E7EB"
              radius={[4, 4, 0, 0]}
              name={t('charts.remaining')}
            />
          </BarChart>
        </ResponsiveContainer>
      </View>
      <StackedLegend />
    </View>
  );
};

// Radar chart colors for each metric - matching Figma design
const RADAR_COLORS = {
  strategicGoals: '#46EDD5',      // Cyan - الأهداف الاستراتيجية
  initiatives: '#00A63E',         // Green - المبادرات الاستراتيجية
  processes: '#EC003F',           // Red - العمليات
  services: '#FFB86A',            // Orange - الخدمات
  unitGoals: '#AD46FF',           // Purple - أهداف الوحدات التنظيمية
  humanCapital: '#8EC5FF',        // Light Blue - رأس المال البشري
};

// Radar chart metric labels with Figma colors (labels are translated dynamically in components)
const RADAR_METRICS = [
  { key: 'strategicGoals', labelKey: 'reports.strategicGoals', color: RADAR_COLORS.strategicGoals },
  { key: 'initiatives', labelKey: 'reports.strategicInitiatives', color: RADAR_COLORS.initiatives },
  { key: 'processes', labelKey: 'sidebar.processes', color: RADAR_COLORS.processes },
  { key: 'services', labelKey: 'sidebar.services', color: RADAR_COLORS.services },
  { key: 'unitGoals', labelKey: 'sidebar.unitGoals', color: RADAR_COLORS.unitGoals },
  { key: 'humanCapital', labelKey: 'reports.humanCapital', color: RADAR_COLORS.humanCapital },
];

// Radar fill color (the polygon)
const RADAR_FILL_COLOR = '#EC003F';

// Custom dot component for multi-colored radar points
const RadarCustomDot: React.FC<any> = (props) => {
  const { cx, cy, index } = props;
  const colors = [
    RADAR_COLORS.strategicGoals,  // Cyan
    RADAR_COLORS.initiatives,      // Green
    RADAR_COLORS.processes,        // Red
    RADAR_COLORS.services,         // Orange
    RADAR_COLORS.unitGoals,        // Purple
    RADAR_COLORS.humanCapital,     // Light Blue
  ];
  const color = colors[index] || RADAR_FILL_COLOR;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={color}
      stroke="#FFFFFF"
      strokeWidth={2}
    />
  );
};

// Organization Radar Card Component for المقارنة الشاملة متعددة الأبعاد
// Vertical layout: Chart on top, Legend below in 2-column grid (matching Figma)
const OrganizationRadarCard: React.FC<{
  org: OrganizationReport;
  onPress: () => void;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, onPress, translateOrg, t }) => {
  const { colors } = useTheme();
  const cardStyle = { backgroundColor: colors.card, borderColor: colors.cardBorder };

  // Translated radar metric labels
  const translatedMetrics = [
    { key: 'strategicGoals', label: t('reports.strategicGoals'), color: RADAR_COLORS.strategicGoals },
    { key: 'initiatives', label: t('reports.strategicInitiatives'), color: RADAR_COLORS.initiatives },
    { key: 'processes', label: t('sidebar.processes'), color: RADAR_COLORS.processes },
    { key: 'services', label: t('sidebar.services'), color: RADAR_COLORS.services },
    { key: 'unitGoals', label: t('sidebar.unitGoals'), color: RADAR_COLORS.unitGoals },
    { key: 'humanCapital', label: t('reports.humanCapital'), color: RADAR_COLORS.humanCapital },
  ];

  // Use real metrics data from API
  const radarData = [
    { category: translatedMetrics[0].label, value: Number(Number(org.strategic_goals_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[1].label, value: Number(Number(org.initiatives_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[2].label, value: Number(Number(org.processes_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[3].label, value: Number(Number(org.services_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[4].label, value: Number(Number(org.unit_goals_achievement || 0).toFixed(2)), fullMark: 100 },
    { category: translatedMetrics[5].label, value: Number(Number(org.human_capital_achievement || 0).toFixed(2)), fullMark: 100 },
  ];

  // Legend items with values from data
  const legendItems = translatedMetrics.map((metric, index) => ({
    label: metric.label,
    value: `%${radarData[index].value}`,
    color: metric.color,
  }));

  return (
    <TouchableOpacity style={[styles.radarCard, cardStyle]} onPress={onPress}>
      {/* Organization Name Header */}
      <View style={styles.radarCardHeader}>
        <Text style={styles.radarCardTitle}>{translateOrg(org.name)}</Text>
      </View>

      {/* Vertical layout: Chart on top, Legend below */}
      <View style={styles.radarCardContent}>
        {/* Radar Chart - Centered, with proper domain scaling */}
        <View style={styles.radarChartWrapper}>
          <ResponsiveContainer width="100%" height={140} debounce={50}>
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis
                dataKey="category"
                tick={false}
              />
              {/* PolarRadiusAxis ensures values scale from 0-100 */}
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={false}
                axisLine={false}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <View style={styles.radarTooltip}>
                        <Text style={styles.radarTooltipLabel}>{payload[0].payload.category}</Text>
                        <Text style={styles.radarTooltipValue}>%{payload[0].value}</Text>
                      </View>
                    );
                  }
                  return null;
                }}
              />
              <Radar
                name={t('reports.achievementPercentage')}
                dataKey="value"
                stroke={RADAR_FILL_COLOR}
                fill={RADAR_FILL_COLOR}
                fillOpacity={0.3}
                strokeWidth={2}
                dot={<RadarCustomDot />}
              />
            </RadarChart>
          </ResponsiveContainer>
        </View>

        {/* Legend BELOW chart - RTL: [Value] [---] [Label] [●] */}
        <View style={styles.radarLegendContainer}>
          {legendItems.map((item, index) => (
            <View key={index} style={styles.radarLegendRow}>
              <Text style={styles.radarLegendValue} numberOfLines={1}>{item.value}</Text>
              <View style={styles.radarLegendDash} />
              <Text style={styles.radarLegendLabel} numberOfLines={1}>{item.label}</Text>
              <View style={[styles.radarLegendDot, { backgroundColor: item.color }]} />
            </View>
          ))}
        </View>
      </View>
    </TouchableOpacity>
  );
};

// Pagination Component (mirrors SchedulesManagementScreen pagination)
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  t: (key: string, options?: any) => string;
}> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange, t }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;

  if (totalPages <= 0) return null;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const paginationText = t('reports.showing', { start: startItem, end: endItem, total: totalItems });

  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <View style={[styles.paginationRow, { flexDirection: rtlRow }]}>
      {/* Showing X-Y of Z - right side */}
      {totalItems > 0 && (
        <Text style={styles.paginationInfoText}>{paginationText}</Text>
      )}

      {/* Spacer */}
      <View style={{ flex: 1 }} />

      {/* Pagination controls - left side */}
      <View style={[styles.paginationControls, { flexDirection: rtlRow }]}>
        {/* Previous button */}
        <TouchableOpacity
          style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
          onPress={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <Text style={styles.paginationButtonText}>{t('reports.previous')}</Text>
          <Ionicons
            name="chevron-forward"
            size={16}
            color={currentPage === 1 ? COLORS.neutral300 : COLORS.neutral600}
          />
        </TouchableOpacity>

        {/* Page numbers */}
        <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
          {getPageNumbers().map((page) => (
            <TouchableOpacity
              key={page}
              style={[
                styles.paginationNumber,
                currentPage === page && styles.paginationNumberActive,
              ]}
              onPress={() => onPageChange(page)}
            >
              <Text
                style={[
                  styles.paginationNumberText,
                  currentPage === page && styles.paginationNumberTextActive,
                ]}
              >
                {page}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Next button */}
        <TouchableOpacity
          style={[styles.paginationButton, currentPage === totalPages && styles.paginationButtonDisabled]}
          onPress={() => currentPage < totalPages && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <Ionicons
            name="chevron-back"
            size={16}
            color={currentPage === totalPages ? COLORS.neutral300 : COLORS.neutral600}
          />
          <Text style={styles.paginationButtonText}>{t('reports.next')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default function ExportScreen() {
  const navigation = useNavigation();
  const { canExport } = useRoleNavigation();
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Theme-aware styles for cards
  const themedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
  };

  // Responsive breakpoints
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Comparison data (powers the Reports table + the charts tab). Cached via
  // React Query — re-visiting /export shows the previous result instantly
  // instead of re-fetching and re-rendering a spinner. Year-context
  // invalidates the `['strategy', ...]` family so a year change still
  // triggers a refetch on this screen.
  const {
    data: comparisonRaw,
    isPending: loading,
    isRefetching,
    refetch: refetchComparison,
  } = useStrategyComparisonQuery({
    sortBy: 'vision_achievement',
    order: 'desc',
    enabled: canExport,
  });
  const [refreshing, setRefreshing] = useState(false);
  const organizations = useMemo<OrganizationReport[]>(() => {
    const list = comparisonRaw ?? [];
    return list.map((org, index) => ({
      ...org,
      rank: index + 1,
      rating: getRating(org.vision_achievement),
    }));
  }, [comparisonRaw]);

  // Filters state
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedCriteria, setSelectedCriteria] = useState('all');

  // Sort state for the OrganizationsComparisonSection (مقارنة أداء الجهات)
  // chart in رسومات بيانية. Defaults match the dashboard.
  const [comparisonSortBy, setComparisonSortBy] = useState<string>('commitment_index');
  const [comparisonOrder, setComparisonOrder] = useState<'asc' | 'desc'>('desc');
  const handleComparisonSortChange = useCallback(
    (field: string, nextOrder: 'asc' | 'desc') => {
      setComparisonSortBy(field);
      setComparisonOrder(nextOrder);
    },
    [],
  );

  // Date filter state for charts
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6); // 6 months ago
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());

  // Format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Redirect unauthorized users (only strategy_team, executive, and superuser can view reports)
  useEffect(() => {
    if (!canExport) {
      console.log('Reports: Unauthorized access (only strategy_team, executive, and superuser allowed) - redirecting');
      navigation.goBack();
    }
  }, [canExport, navigation]);

  // Manual pull-to-refresh — bypass the React Query cache and force a fresh
  // fetch. Year-driven refetches happen automatically via YearContext's
  // `['strategy']` invalidation.
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchComparison();
    } finally {
      setRefreshing(false);
    }
  }, [refetchComparison]);

  // Derived filtered + sorted organizations. Auto-applies whenever any filter
  // state changes, so picking from a dropdown updates the table/charts
  // immediately. The "Apply Filter" button just snaps back to page 1.
  const filteredOrgs = useMemo<OrganizationReport[]>(() => {
    let result = [...organizations];

    // Filter by organization (single-org pick)
    if (selectedOrg !== 'all') {
      result = result.filter((o) => o.id.toString() === selectedOrg);
    }

    // Sort by criteria — default ranking is by vision_achievement (desc)
    if (selectedCriteria === 'kpis') {
      result.sort((a, b) => (b.total_kpis || 0) - (a.total_kpis || 0));
    } else if (selectedCriteria === 'commitment') {
      result.sort((a, b) => (b.commitment_index || 0) - (a.commitment_index || 0));
    }

    // Re-rank after filter/sort so rank cells match the visible order
    return result.map((org, idx) => ({ ...org, rank: idx + 1 }));
  }, [organizations, selectedOrg, selectedCriteria]);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrg, selectedCriteria]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
  };

  // ────────────────────────────────────────────────────────────────
  // DTS UC-07 — Excel export of the current report state.
  // ────────────────────────────────────────────────────────────────
  // PDF was tried with a print stylesheet + DOM wrapper in commit
  // 9907f46 — but RN-Web's <View> doesn't pass `data-*` / `className`
  // through cleanly via spread, which silently broke the whole
  // screen render. This version keeps the page rendering safe and
  // adds Excel only; PDF will be added back via window.print() with
  // a CSS class added imperatively (no JSX changes).
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const handleExportExcel = useCallback(() => {
    if (isExportingExcel) return;
    setIsExportingExcel(true);
    try {
      const wb = XLSX.utils.book_new();
      const generatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');

      // Sheet 1: ملخص تنفيذي
      const totalKpis = organizations.reduce((s, o: any) => s + (o.total_kpis || 0), 0);
      const totalInits = organizations.reduce((s, o: any) => s + (o.total_initiatives || 0), 0);
      const totalGoals = organizations.reduce((s, o: any) => s + (o.total_goals || 0), 0);
      const summary: (string | number)[][] = [
        ['نظام إدارة الأداء المؤسسي — تقرير ديناميكي'],
        [`السنة: ${selectedYear}`],
        [`تاريخ التوليد: ${generatedAt}`],
        [],
        ['المؤشر', 'القيمة'],
        ['عدد الجهات', organizations.length],
        ['إجمالي المؤشرات الاستراتيجية', totalKpis],
        ['إجمالي العناصر التشغيلية', totalInits],
        ['إجمالي الأهداف الاستراتيجية', totalGoals],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      ws1['!cols'] = [{ wch: 40 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'ملخص تنفيذي');

      // Sheet 2: مقارنة الجهات
      const rows: (string | number)[][] = [
        ['الترتيب', 'الجهة', 'مؤشر الالتزام (%)', 'تحقيق الرؤية (%)',
         'الأهداف', 'العناصر', 'المؤشرات', 'التقييم'],
        ...filteredOrgs.map((o: any) => [
          o.rank,
          translateOrg(o.organization_name || o.name || '') || '',
          Number((o.commitment_index || 0).toFixed(2)),
          Number((o.vision_achievement || 0).toFixed(2)),
          o.total_goals || 0,
          o.total_initiatives || 0,
          o.total_kpis || 0,
          getRatingLabel(o.rating, t),
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(rows);
      ws2['!cols'] = [
        { wch: 8 }, { wch: 35 }, { wch: 18 }, { wch: 18 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, 'مقارنة الجهات');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `CPMS-Report-${selectedYear}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
      window.alert('فشل تصدير ملف Excel.');
    } finally {
      setIsExportingExcel(false);
    }
  }, [isExportingExcel, organizations, filteredOrgs, selectedYear, t, translateOrg]);

  // PDF: just trigger the browser's print dialog. The user picks
  // "Save as PDF" from there. No DOM injection, no class toggling
  // — the print stylesheet is a global CSS rule we ship in
  // public/index.html (or just shows everything; user can crop in
  // their PDF viewer).
  const handleExportPdf = useCallback(() => {
    if (typeof window !== 'undefined' && typeof window.print === 'function') {
      window.print();
    }
  }, []);

  // Calculate pagination from filtered list
  const totalPages = Math.max(1, Math.ceil(filteredOrgs.length / itemsPerPage));
  const paginatedOrgs = filteredOrgs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (!canExport) {
    return null;
  }

  // No more blocking ActivityIndicator on every cold visit. The query has
  // `placeholderData: keepPreviousData`, so revisits show cached rows
  // immediately. On the very first visit the page below renders an empty
  // shell while data loads — far better UX than a full-screen spinner.

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('navigation.reports') },
        ]}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[COLORS.primary]} />
        }
      >
        {/* Page Title */}
        {/* Page title + export buttons. Kept as a flat row of native
            components (no className/data-* spreads on <View>) so the
            React Native Web render path stays clean. */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 8,
        }}>
          <Text style={[styles.pageTitle, isMobile && { fontSize: 18 }]}>{t('reports.mainDashboard')}</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              onPress={handleExportPdf}
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 6,
                backgroundColor: '#A9211F',
                paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8,
              }}
            >
              <Ionicons name="document-text-outline" size={16} color="#FFF" />
              <Text style={{ color: '#FFF', fontFamily: FONTS.medium, fontSize: 13 }}>
                تصدير PDF
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleExportExcel}
              disabled={isExportingExcel}
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 6,
                backgroundColor: isExportingExcel ? '#9CA3AF' : '#16A34A',
                paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8,
              }}
            >
              <Ionicons name="grid-outline" size={16} color="#FFF" />
              <Text style={{ color: '#FFF', fontFamily: FONTS.medium, fontSize: 13 }}>
                {isExportingExcel ? 'جاري التصدير...' : 'تصدير Excel'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Stats Cards Row 1 - Counts (with icons) */}
        <View style={[styles.statsCardsRow, isMobile && styles.statsCardsRowMobile]}>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('dashboard.strategicKPIs')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/chart-column-big.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_kpis || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.strategicInitiatives')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/rocket.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_initiatives || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.strategicGoals')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/goal.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>
              {organizations.reduce((sum, org) => sum + (org.total_goals || 0), 0)}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeader}>
              <Text style={styles.statsCardLabel}>{t('reports.organizations')}</Text>
              <View style={styles.statsCardIconBox}>
                <img
                  src={require('../../../assets/Icon-svg/building-2.svg')}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </View>
            </View>
            <Text style={styles.statsCardValue}>{organizations.length}</Text>
          </View>
        </View>

        {/* Stats Cards Row 2 - Performance (no icons, just labels) */}
        <View style={[styles.statsCardsRow, isMobile && styles.statsCardsRowMobile]}>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsNeedImprovement')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#D62728' }]}>
              {organizations.filter(o => o.rating === 'poor').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsAverage')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#FF7F0E' }]}>
              {organizations.filter(o => o.rating === 'average').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.orgsExcellent')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#2CA02C' }]}>
              {organizations.filter(o => o.rating === 'excellent').length}
            </Text>
          </View>
          <View style={[styles.statsCard, themedCardStyles.card]}>
            <View style={styles.statsCardHeaderSimple}>
              <Text style={styles.statsCardLabel}>{t('reports.averageOverallPerformance')}</Text>
            </View>
            <Text style={[styles.statsCardValue, { color: '#FF7F0E' }]}>
              %{organizations.length > 0
                ? formatPct(organizations.reduce((sum, org) => sum + org.vision_achievement, 0) / organizations.length)
                : '0.00'}
            </Text>
          </View>
        </View>

        {/* Tab Content Container */}
        <View style={[styles.tabContent, themedCardStyles.card, isMobile && styles.tabContentMobile]}>
          {/* Tabs - aligned to right */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={[styles.tabsContainer, isMobile && styles.tabsContainerMobile]}
          >
            <TabButton
              label={t('reports.rankingAndRating')}
              isActive={activeTab === 'ranking'}
              onPress={() => setActiveTab('ranking')}
            />
            <TabButton
              label={t('reports.charts')}
              isActive={activeTab === 'charts'}
              onPress={() => setActiveTab('charts')}
            />
            <TabButton
              label={t('reports.detailedComparison')}
              isActive={activeTab === 'comparison'}
              onPress={() => setActiveTab('comparison')}
            />
            <TabButton
              label={t('reports.overview')}
              isActive={activeTab === 'overview'}
              onPress={() => setActiveTab('overview')}
            />
          </ScrollView>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Section Title */}
              <Text style={styles.sectionTitle}>{t('reports.orgsPerformanceOverview')}</Text>

            {/* Filters Row */}
            <View style={[styles.filtersRow, isMobile && styles.filtersRowMobile]}>
              {/* Filter Button - Left side */}
              <TouchableOpacity style={styles.applyFilterButton} onPress={handleApplyFilter}>
                <Text style={styles.applyFilterText}>{t('reports.applyFilter')}</Text>
              </TouchableOpacity>

              {/* Filters - Right side */}
              {Platform.OS === 'web' && (
                <View style={styles.filtersGroup}>
                  <FilterDropdown
                    label={t('reports.criteria')}
                    value={selectedCriteria}
                    options={[
                      { value: 'all', label: t('reports.allCriteria') },
                      { value: 'commitment', label: t('organizations.commitmentIndexLabel') },
                      { value: 'kpis', label: t('kpis.title') },
                    ]}
                    onChange={setSelectedCriteria}
                  />
                  <FilterDropdown
                    label={t('reports.organization')}
                    value={selectedOrg}
                    options={[
                      { value: 'all', label: t('reports.allOrganizations') },
                      ...organizations.map(org => ({ value: org.id.toString(), label: translateOrg(org.name) })),
                    ]}
                    onChange={setSelectedOrg}
                  />
                </View>
              )}
            </View>

            {/* Table - RTL direction */}
            <ScrollView
              horizontal={isMobile}
              showsHorizontalScrollIndicator={isMobile}
              contentContainerStyle={{ flexGrow: 1 }}
              style={{ width: '100%' }}
            >
              <View style={[styles.tableContainer, { width: '100%' }, isMobile && { minWidth: 900 }]}>
                {/* Table Header - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view */}
                <View style={styles.tableHeader}>
                  <View style={[styles.tableHeaderCell, styles.rankCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.rank')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.nameCell]}>
                    <Text style={[styles.tableHeaderText, styles.tableHeaderTextRight]}>{t('reports.organization')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiCountCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.kpiCount')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.good')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.average')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.kpiStatusCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.poor')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.achievementCell]}>
                    <Text style={styles.tableHeaderText}>{t('organizations.commitmentIndexLabel')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.initiativesCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.completedInitiatives')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.ratingCell]}>
                    <Text style={styles.tableHeaderText}>{t('reports.rating')}</Text>
                  </View>
                  <View style={[styles.tableHeaderCell, styles.viewCell]}>
                    <Text style={styles.tableHeaderText}></Text>
                  </View>
                </View>

                {/* Table Body */}
                {paginatedOrgs.map((org) => (
                  <TableRow
                    key={org.id}
                    org={org}
                    onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                    translateOrg={translateOrg}
                    t={t}
                  />
                ))}
              </View>
            </ScrollView>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredOrgs.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              t={t}
            />
            </>
          )}

          {/* Other tabs - placeholder content */}
          {activeTab === 'comparison' && (
            <View style={styles.comparisonTabContent}>
            {/* Organizations Grid - responsive columns */}
            <View style={[styles.comparisonGrid, isMobile && styles.comparisonGridMobile]}>
              {filteredOrgs.map((org) => (
                <ComparisonCard
                  key={org.id}
                  org={org}
                  onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                  translateOrg={translateOrg}
                  t={t}
                />
              ))}
              </View>
            </View>
          )}

          {activeTab === 'charts' && (
            <View style={[styles.chartsTabContent, themedCardStyles.card, isMobile && styles.chartsTabContentMobile]}>
            {/* First Chart Section - توزيع حالة مؤشرات أداء الجهات */}
            <Text style={styles.chartSectionTitle}>{t('reports.kpiStatusDistribution')}</Text>

            {/* Filters Row */}
            <View style={[styles.chartFiltersRow, isMobile && styles.chartFiltersRowMobile]}>
              {Platform.OS === 'web' && (
                <>
                  <View style={styles.chartDateFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.toDate')}</Text>
                    <View style={styles.chartDateInput}>
                      <input
                        type="date"
                        value={formatDateForInput(toDate)}
                        onChange={(e) => setToDate(new Date(e.target.value))}
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          fontSize: 13,
                          color: COLORS.neutral700,
                          fontFamily: 'JF-Flat-Regular',
                          textAlign: 'center',
                          outline: 'none',
                          width: '100%',
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>
                  <View style={styles.chartDateFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.fromDate')}</Text>
                    <View style={styles.chartDateInput}>
                      <input
                        type="date"
                        value={formatDateForInput(fromDate)}
                        onChange={(e) => setFromDate(new Date(e.target.value))}
                        style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          fontSize: 13,
                          color: COLORS.neutral700,
                          fontFamily: 'JF-Flat-Regular',
                          textAlign: 'center',
                          outline: 'none',
                          width: '100%',
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>
                  <View style={styles.chartOrgFilter}>
                    <Text style={styles.chartFilterLabel}>{t('reports.organizations')}</Text>
                    <FilterDropdown
                      label={t('reports.organizations')}
                      value={selectedOrg}
                      options={[
                        { value: 'all', label: t('reports.allOrganizations') },
                        ...organizations.map(org => ({ value: org.id.toString(), label: translateOrg(org.name) })),
                      ]}
                      onChange={setSelectedOrg}
                    />
                  </View>
                </>
              )}
            </View>

            {/* Stacked Bar Chart */}
            <StackedBarChart data={filteredOrgs} height={isMobile ? 300 : 350} translateOrg={translateOrg} t={t} />

            {/* Divider */}
            <View style={styles.chartDivider} />

            {/* Second Chart Section - مقارنة أداء الجهات
                Reuses the dashboard's OrganizationsComparisonSection — title,
                date/org filters, sort buttons, tooltip and chart card all come
                from that component, so there is nothing else to render here. */}
            <OrganizationsComparisonSection
              data={organizations}
              loading={loading}
              sortBy={comparisonSortBy}
              order={comparisonOrder}
              onSortChange={handleComparisonSortChange}
            />

            {/* Divider */}
            <View style={styles.chartDivider} />

            {/* Third Section - المقارنة الشاملة متعددة الأبعاد */}
            <Text style={styles.chartSectionTitle}>{t('reports.comprehensiveMultiDimensional')}</Text>

            {/* Radar Charts Grid */}
            <View style={[styles.radarCardsGrid, isMobile && styles.radarCardsGridMobile]}>
              {filteredOrgs.map((org) => (
                <OrganizationRadarCard
                  key={org.id}
                  org={org}
                  onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                  translateOrg={translateOrg}
                  t={t}
                />
              ))}
              </View>
            </View>
          )}

          {activeTab === 'ranking' && (
            <View style={styles.rankingTabContent}>
            {/* Two Column Layout - RTL: Evaluation on left, Ranking on right */}
            <View style={[styles.rankingColumnsContainer, isMobile && styles.rankingColumnsContainerMobile]}>
              {/* Left Column - التقييم النهائي (Final Evaluation) */}
              <View style={styles.rankingEvaluationColumn}>
                <View style={[styles.rankingCard, themedCardStyles.card]}>
                  <Text style={styles.rankingCardTitle}>{t('reports.finalEvaluation')}</Text>

                  {/* Rating Distribution Table */}
                  <View style={styles.evaluationTable}>
                    {/* Table Header */}
                    <View style={styles.evaluationTableHeader}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.percentage')}</Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.orgCount')}</Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <Text style={styles.evaluationHeaderText}>{t('reports.category')}</Text>
                      </View>
                    </View>

                    {/* Excellent Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'excellent').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'excellent').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: '#DCFCE7' }]}>
                          <Text style={[styles.evaluationCategoryText, { color: '#22C55E' }]}>{t('charts.excellent')}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Average Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'average').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'average').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: '#FEF3C7' }]}>
                          <Text style={[styles.evaluationCategoryText, { color: '#F59E0B' }]}>{t('reports.average')}</Text>
                        </View>
                      </View>
                    </View>

                    {/* Poor Row */}
                    <View style={styles.evaluationTableRow}>
                      <View style={[styles.evaluationTableCell, styles.evaluationPercentCell]}>
                        <Text style={styles.evaluationCellText}>
                          %{organizations.length > 0
                            ? formatPct((organizations.filter(o => o.rating === 'poor').length / organizations.length) * 100)
                            : '0.00'}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCountCell]}>
                        <Text style={styles.evaluationCellText}>
                          {organizations.filter(o => o.rating === 'poor').length}
                        </Text>
                      </View>
                      <View style={[styles.evaluationTableCell, styles.evaluationCategoryCell]}>
                        <View style={[styles.evaluationCategoryBadge, { backgroundColor: '#FEE2E2' }]}>
                          <Text style={[styles.evaluationCategoryText, { color: '#EF4444' }]}>{t('reports.poor')}</Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Recommendations Section */}
                  <View style={styles.recommendationsSection}>
                    <Text style={styles.recommendationsTitle}>{t('reports.recommendations')}</Text>
                    <View style={styles.recommendationsList}>
                      {organizations.filter(o => o.rating === 'poor').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationPoor', { count: organizations.filter(o => o.rating === 'poor').length })}
                          </Text>
                        </View>
                      )}
                      {organizations.filter(o => o.rating === 'average').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationAverage')}
                          </Text>
                        </View>
                      )}
                      {organizations.filter(o => o.rating === 'excellent').length > 0 && (
                        <View style={styles.recommendationItem}>
                          <View style={styles.recommendationBullet} />
                          <Text style={styles.recommendationText}>
                            {t('reports.recommendationExcellent')}
                          </Text>
                        </View>
                      )}
                      <View style={styles.recommendationItem}>
                        <View style={styles.recommendationBullet} />
                        <Text style={styles.recommendationText}>
                          {t('reports.recommendationCoordination')}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>

              {/* Right Column - ترتيب الجهات حسب الأداء (Organization Ranking) */}
              <View style={styles.rankingListColumn}>
                <View style={[styles.rankingCard, themedCardStyles.card]}>
                  <Text style={styles.rankingCardTitle}>{t('reports.orgRankingByPerformance')}</Text>

                  {/* Ranking List */}
                  <View style={styles.rankingList}>
                    {filteredOrgs.map((org, index) => (
                      <TouchableOpacity
                        key={org.id}
                        style={[
                          styles.rankingListItem,
                          index === 0 && styles.rankingListItemFirst,
                        ]}
                        onPress={() => (navigation as any).navigate('OrganizationDetail', { id: org.id })}
                      >
                        <View style={styles.rankingItemContent}>
                          <Text style={[
                            styles.rankingItemPercentage,
                            index === 0 && styles.rankingItemPercentageFirst,
                          ]}>
                            %{formatPct(org.vision_achievement)}
                          </Text>
                          <Text style={[
                            styles.rankingItemName,
                            index === 0 && styles.rankingItemNameFirst,
                          ]} numberOfLines={1}>
                            {translateOrg(org.name)}
                          </Text>
                        </View>
                        <View style={[
                          styles.rankingItemNumber,
                          index === 0 && styles.rankingItemNumberFirst,
                        ]}>
                          <Text style={[
                            styles.rankingItemNumberText,
                            index === 0 && styles.rankingItemNumberTextFirst,
                          ]}>
                            {index + 1}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                </View>
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 24,
    paddingBottom: 40,
  },
  scrollContentMobile: {
    padding: 12,
    paddingBottom: 30,
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 20,
    fontFamily: 'JF-Flat-Medium',
  },
  statsCardsRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 20,
  },
  statsCardsRowMobile: {
    flexWrap: 'wrap',
    gap: 12,
  },
  statsCard: {
    flex: 1,
    minWidth: 150,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  statsCardHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  statsCardHeaderSimple: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 40,
    marginBottom: 16,
  },
  statsCardLabel: {
    fontSize: 14,
    color: '#414042',
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'right',
  },
  statsCardIconBox: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statsCardValue: {
    fontSize: 30,
    fontWeight: '700',
    color: '#414042',
    textAlign: 'center',
    fontFamily: FONTS.numbers,
  } as any,
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  tabsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 24,
    justifyContent: 'flex-end',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    flexGrow: 1,
  },
  tabsContainerMobile: {
    paddingHorizontal: 8,
  },
  tabButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: 'transparent',
    borderRadius: 8,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#A6813F',
  },
  tabButtonText: {
    fontSize: 14,
    color: '#334155',
    fontFamily: FONTS.medium,
    fontWeight: '700',
  },
  tabButtonTextActive: {
    color: '#FFFFFF',
  },
  tabContent: {
    borderRadius: 12,
    padding: 24,
  },
  tabContentMobile: {
    padding: 12,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.neutral900,
    marginBottom: 16,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Medium',
  },
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  filtersRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  filtersGroup: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterContainer: {
    position: 'relative',
    minWidth: 140,
  },
  filterSelect: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 36,
    paddingRight: 12,
    fontSize: 14,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'right',
    width: '100%',
  } as any,
  filterArrow: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -8 }],
  },
  applyFilterButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  applyFilterText: {
    fontSize: 14,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
  },
  tableContainer: {
    width: '100%',
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row-reverse',
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 4,
  },
  tableHeaderCell: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    textAlign: 'center',
    fontFamily: 'JF-Flat-Medium',
    whiteSpace: 'nowrap',
  } as any,
  tableHeaderTextRight: {
    textAlign: 'right',
  },
  tableRow: {
    flexDirection: 'row-reverse',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableCell: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  tableCellText: {
    fontSize: 14,
    color: COLORS.neutral700,
    textAlign: 'center',
    fontFamily: FONTS.numbers,
  },
  // Column widths - RTL order: الترتيب | الجهة | عدد المؤشرات | جيد | متوسط | ضعيف | نسبة الإنجاز | المبادرات المكتملة | التقييم | view
  rankCell: {
    width: 70,
    alignItems: 'center',
  },
  nameCell: {
    flex: 1,
    minWidth: 200,
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
  kpiCountCell: {
    width: 120,
    alignItems: 'center',
  },
  kpiStatusCell: {
    width: 70,
    alignItems: 'center',
  },
  achievementCell: {
    width: 120,
    alignItems: 'center',
  },
  initiativesCell: {
    width: 140,
    alignItems: 'center',
  },
  ratingCell: {
    width: 90,
    alignItems: 'center',
  },
  viewCell: {
    width: 50,
    alignItems: 'center',
  },
  // Cell content styles
  orgName: {
    fontSize: 13,
    color: COLORS.neutral900,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Regular',
  },
  rankText: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: FONTS.numbers,
  },
  viewButton: {
    padding: 4,
  },
  ratingBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 4,
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  ratingText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: 'JF-Flat-Medium',
  },
  achievementCellContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  achievementText: {
    fontSize: 13,
    color: COLORS.neutral700,
    fontFamily: FONTS.numbers,
  },
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    marginTop: 16,
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 4,
  },
  paginationButtonDisabled: {
    opacity: 0.4,
  },
  paginationButtonText: {
    fontSize: 13,
    color: COLORS.neutral600,
    fontFamily: FONTS.regular,
  },
  paginationNumbers: {
    flexDirection: 'row',
    gap: 4,
  },
  paginationNumber: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationNumberActive: {
    borderColor: '#334155',
  },
  paginationNumberText: {
    fontSize: 13,
    color: COLORS.neutral600,
    fontFamily: FONTS.numbers,
  },
  paginationNumberTextActive: {
    color: COLORS.neutral900,
    fontFamily: FONTS.medium,
  },
  paginationInfoText: {
    fontSize: 12,
    color: COLORS.neutral500,
    fontFamily: FONTS.regular,
  },
  comingSoonContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  comingSoonTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.neutral900,
    marginTop: 16,
    marginBottom: 8,
    fontFamily: 'JF-Flat-Medium',
  },
  comingSoonText: {
    fontSize: 14,
    color: COLORS.neutral500,
    fontFamily: 'JF-Flat-Regular',
  },

  // Comparison Tab Styles (مقارنة تفصيلية)
  comparisonTabContent: {
    padding: 0,
  },
  comparisonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'flex-end',
  },
  comparisonGridMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  comparisonCard: {
    width: '31.5%',
    minWidth: 280,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
  },
  comparisonCardHeader: {
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  comparisonCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Medium',
    flex: 1,
  },
  comparisonRatingBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 4,
    borderWidth: 1,
    backgroundColor: 'transparent',
    marginLeft: 8,
  },
  comparisonRatingText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: 'JF-Flat-Medium',
  },
  comparisonStatsGrid: {
    padding: 12,
    gap: 8,
  },
  comparisonProgressSection: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  comparisonProgressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  comparisonProgressLabel: {
    fontSize: 12,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonProgressValue: {
    fontSize: 16,
    fontWeight: '600',
    fontFamily: FONTS.numbers,
  },
  comparisonProgressBarBg: {
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
  },
  comparisonProgressBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  comparisonStatsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  comparisonStatBox: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  statBoxDefault: {
    backgroundColor: '#F3F4F6',
  },
  statBoxGreen: {
    backgroundColor: '#DCFCE7',
  },
  statBoxYellow: {
    backgroundColor: '#FEF3C7',
  },
  statBoxRed: {
    backgroundColor: '#FEE2E2',
  },
  comparisonStatLabel: {
    fontSize: 11,
    color: COLORS.neutral600,
    marginBottom: 2,
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonStatValue: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
  },
  comparisonBottomStats: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  comparisonBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  comparisonBottomLabel: {
    fontSize: 12,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  comparisonBottomValue: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
  },

  // Charts Tab Styles (رسومات بيانية)
  chartsTabContent: {
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
  },
  chartsTabContentMobile: {
    padding: 12,
    borderRadius: 8,
  },
  chartSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 16,
    fontFamily: 'JF-Flat-Medium',
  },
  chartFiltersRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  chartFiltersRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  chartDateFilter: {
    gap: 6,
  },
  chartFilterLabel: {
    fontSize: 12,
    color: COLORS.neutral600,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Regular',
  },
  chartDateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  chartOrgFilter: {
    gap: 6,
    minWidth: 160,
  },
  chartContainer: {
    backgroundColor: '#FAFAFA',
    borderRadius: 8,
    padding: 16,
    minWidth: 300,
    minHeight: 350,
  },
  chartDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 32,
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 12,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },

  // Tooltip Styles
  tooltipContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  tooltipLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.neutral900,
    marginBottom: 8,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Medium',
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginVertical: 2,
  },
  tooltipDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  tooltipText: {
    fontSize: 12,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
  },

  // Radar Chart Styles (المقارنة الشاملة متعددة الأبعاد)
  // Vertical layout: Chart on top, Legend below in 2-column grid (matching Figma)
  radarCardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'flex-end',
  },
  radarCardsGridMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  radarCard: {
    width: '23.5%',
    minWidth: 240,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    overflow: 'hidden',
  },
  radarCardHeader: {
    marginBottom: 8,
  },
  radarCardTitle: {
    fontSize: 10,
    color: COLORS.neutral700,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Regular',
  },
  radarCardContent: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  radarChartWrapper: {
    width: '100%',
    minWidth: 200,
    minHeight: 140,
    alignItems: 'center',
  },
  radarLegendContainer: {
    width: '100%',
    gap: 6,
  },
  radarLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: 8,
  },
  radarLegendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  radarLegendLabel: {
    fontSize: 12,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    flexShrink: 0,
  },
  radarLegendDash: {
    flex: 1,
    height: 0,
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#94A3B8',
    minWidth: 8,
  },
  radarLegendValue: {
    fontSize: 12,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    flexShrink: 0,
  },
  radarTooltip: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  radarTooltipLabel: {
    fontSize: 11,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'right',
    marginBottom: 2,
  },
  radarTooltipValue: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.neutral900,
    fontFamily: 'JF-Flat-Medium',
    textAlign: 'right',
  },

  // Ranking Tab Styles (الترتيب والتقييم)
  rankingTabContent: {
    padding: 0,
  },
  rankingColumnsContainer: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  rankingColumnsContainerMobile: {
    flexDirection: 'column-reverse',
    gap: 16,
  },
  rankingEvaluationColumn: {
    flex: 1,
    minWidth: 300,
  },
  rankingListColumn: {
    flex: 1,
    minWidth: 350,
  },
  rankingCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
  },
  rankingCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 16,
    fontFamily: 'JF-Flat-Medium',
  },

  // Evaluation Table Styles
  evaluationTable: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    overflow: 'hidden',
  },
  evaluationTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  evaluationTableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  evaluationTableCell: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  evaluationCategoryCell: {
    flex: 1,
    alignItems: 'flex-end',
  },
  evaluationCountCell: {
    width: 100,
  },
  evaluationPercentCell: {
    width: 80,
  },
  evaluationHeaderText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Medium',
    textAlign: 'center',
  },
  evaluationCellText: {
    fontSize: 14,
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Regular',
    textAlign: 'center',
  },
  evaluationCategoryBadge: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
  },
  evaluationCategoryText: {
    fontSize: 13,
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },

  // Recommendations Styles
  recommendationsSection: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  recommendationsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 12,
    fontFamily: 'JF-Flat-Medium',
  },
  recommendationsList: {
    gap: 8,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  recommendationBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: COLORS.primary,
    marginTop: 6,
  },
  recommendationText: {
    flex: 1,
    fontSize: 13,
    color: COLORS.neutral700,
    textAlign: 'right',
    lineHeight: 20,
    fontFamily: 'JF-Flat-Regular',
  },

  // Ranking List Styles
  rankingList: {
    gap: 8,
  },
  rankingListItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    gap: 12,
  },
  rankingListItemFirst: {
    backgroundColor: '#FEF3C7',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  rankingItemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  rankingItemName: {
    flex: 1,
    fontSize: 14,
    color: COLORS.neutral700,
    textAlign: 'right',
    fontFamily: 'JF-Flat-Regular',
  },
  rankingItemNameFirst: {
    color: COLORS.neutral900,
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemPercentage: {
    fontSize: 14,
    color: COLORS.neutral600,
    fontFamily: 'JF-Flat-Regular',
  },
  rankingItemPercentageFirst: {
    color: '#B45309',
    fontWeight: '600',
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  rankingItemNumberFirst: {
    backgroundColor: '#F59E0B',
    borderColor: '#F59E0B',
  },
  rankingItemNumberText: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.neutral700,
    fontFamily: 'JF-Flat-Medium',
  },
  rankingItemNumberTextFirst: {
    color: '#FFFFFF',
  },
});
